import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { isAdminUser, canAccessCrm } from "@/lib/api";
import { Home, Users, Ticket, DollarSign, User, ArrowLeft, BookOpenCheck, CalendarDays, Lock, Boxes } from "lucide-react";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

function buildTabs(user) {
  const admin = isAdminUser(user);
  const access = canAccessCrm(user);
  const crmLabel = "CRM";
  // Home and Profile tabs are removed — logo navigates home (Orders) and the
  // avatar menu (top right) covers profile access. CRM / Coupons / Events are
  // gated behind an approved account (Airtable Users.approved = 1).
  const tabs = [
    { to: "/app/contacts", label: crmLabel, icon: Users, testId: "tab-contacts", gated: !access },
    { to: "/app/coupons", label: "Coupons", icon: Ticket, testId: "tab-coupons", gated: !access },
  ];
  if (admin) {
    tabs.push({
      to: "/app/catalogue",
      label: "Catalogue",
      icon: BookOpenCheck,
      testId: "tab-catalogue",
    });
  }
  tabs.push(
    { to: "/app/earnings", label: "Orders", icon: DollarSign, testId: "tab-earnings" },
    { to: "/app/folio", label: "Folio", icon: Boxes, testId: "tab-folio", gated: !access },
    { to: "/app/services", label: "Events", icon: CalendarDays, testId: "tab-events", gated: !access },
  );
  return tabs;
}

export default function AppShell() {
  const { user, ready } = useAuth();
  const location = useLocation();

  if (!ready) {
    return (
      <div className="min-h-dvh bg-white flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-violet-600 border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  return (
    <div className="min-h-dvh bg-zinc-50 flex flex-col" data-testid="app-shell">
      <ImpersonationBanner />
      <TopNav variant="app" />

      {/* Page content */}
      <main className="flex-1 pb-24 sm:pb-8">
        <div className={`${location.pathname.startsWith("/app/sales-report") ? "max-w-7xl" : "max-w-2xl"} mx-auto w-full`}>
          <Outlet />
        </div>
      </main>

      {/* Bottom nav — mobile only (top nav handles tablet+) */}
      <nav
        className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-zinc-200 pb-[env(safe-area-inset-bottom)] sm:hidden"
        data-testid="bottom-nav"
      >
        <ul
          className="max-w-2xl mx-auto grid h-16"
          style={{ gridTemplateColumns: `repeat(${buildTabs(user).length}, minmax(0, 1fr))` }}
        >
          {buildTabs(user).map(({ to, label, icon: Icon, testId, gated }) => (
            <li key={to} className="p-1.5">
              {gated ? (
                <button
                  type="button"
                  onClick={() =>
                    toast.message("Approval required", {
                      description: `${label} is available to approved dealers. Reach out to your sponsor to get your account approved.`,
                    })
                  }
                  data-testid={testId}
                  className="w-full h-full rounded-xl flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-zinc-300"
                  title="Approval required"
                >
                  <Lock size={18} strokeWidth={1.9} />
                  <span>{label}</span>
                </button>
              ) : (
                <NavLink
                  to={to}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `h-full rounded-xl flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors ${
                      isActive
                        ? "bg-violet-600 text-white shadow-sm"
                        : "text-zinc-500 hover:text-violet-700 hover:bg-violet-50"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={20} strokeWidth={isActive ? 2.4 : 1.9} />
                      <span>{label}</span>
                    </>
                  )}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}


/**
 * ImpersonationBanner — shows a red strip across the top whenever an admin
 * is signed in as another user. Click "Stop" to restore the admin session.
 */
function ImpersonationBanner() {
  const { user } = useAuth();
  let isImpersonating = false;
  try {
    isImpersonating = !!localStorage.getItem("postcue.impersonator");
  } catch {
    /* ignore */
  }
  if (!isImpersonating) return null;
  function stop() {
    try {
      const original = localStorage.getItem("postcue.impersonator");
      if (original) {
        localStorage.setItem("postcue.session", original);
        localStorage.removeItem("postcue.impersonator");
      }
    } catch {
      /* ignore */
    }
    window.location.assign("/app/contacts");
  }
  return (
    <div
      className="bg-rose-600 text-white px-4 py-2 text-xs sm:text-sm font-medium flex items-center justify-between gap-2"
      data-testid="impersonation-banner"
    >
      <span>
        ⚠ Signed in as{" "}
        <strong>
          {user?.first_name} {user?.last_name}
        </strong>{" "}
        (admin impersonation)
      </span>
      <button
        onClick={stop}
        className="bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1 flex items-center gap-1"
        data-testid="impersonation-stop"
      >
        <ArrowLeft size={12} />
        Stop
      </button>
    </div>
  );
}
